import React, { useCallback, useEffect, useMemo } from 'react';

import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';

import { dashboardIcon } from 'c-main/icons';

import { PermissionName } from 'c-sdk';
import { Box, IconButton, Stack, Tab, Tooltip, Typography } from '@mui/material';
import { Tabs } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { TabContext, TabPanel } from '@mui/lab';
import FirstTab from 'c-dashboard-module/Tabs/FirstTab';
import SecondTab from 'c-dashboard-module/Tabs/SecondTab';
import DashboardCustomizationDialog from 'c-dashboard-module/DashboardCustomizationDialog';
import { LOCAL_SOURCE_KEY, useDashboardSources } from 'c-dashboard-module/useDashboardSources';
import { DashboardTabLabel } from 'c-dashboard-module/DashboardSourcePicker';
import { useAPIClientRequest, useAuthenticatedUser } from 'c-hooks';
import { useCommonTranslation } from 'c-translation';
import ApiClient from 'c-data/apiClient';
import { useSetAtom } from 'jotai';
import type { DashboardLayoutConfig } from 'c-dashboard-module/dashboardConfig';
import {
    atom_customDashboardConfig,
    getDefaultDashboardConfig,
} from 'c-dashboard-module/dashboardConfig';

const YTD_TAB = '1';
const CUSTOM_TAB = 'custom';
const LIVE_TAB = '2';

const MainDashboardPage = () => {
    const t = useCommonTranslation();
    const currentYear = new Date().getFullYear();
    // The YTD Aggregate tab always renders the built-in dashboard; customisation lives
    // on its own tab — named after the saved configuration it came from, "Custom" otherwise —
    // which only exists once one has been configured.
    const defaultConfig = useMemo(() => getDefaultDashboardConfig(), []);
    // Every dashboard this user can see on the Custom tab: their own, plus any assigned to
    // them. `activeConfig` is whichever the picker has selected.
    const { sources, active, activeKey, activeConfig, select } = useDashboardSources();
    const [value, setValue] = React.useState(YTD_TAB);

    // A dashboard built with the cog is scratch work for this visit: it is dropped when the
    // page unmounts, so navigating away discards it just as a reload does. Anything worth
    // keeping is saved by name from the dialog.
    const setSessionDashboard = useSetAtom(atom_customDashboardConfig);
    useEffect(() => () => setSessionDashboard(null), [setSessionDashboard]);
    // Rearranging the dashboard on screen is an edit like any other: it becomes this visit's own
    // dashboard (an assigned one is forked, keeping its name), saved by name from the cog.
    const onLayoutChange = useCallback(
        (layout: DashboardLayoutConfig) => {
            if (!activeConfig) return;
            setSessionDashboard({ ...activeConfig, layout });
            select(LOCAL_SOURCE_KEY);
        },
        [activeConfig, setSessionDashboard, select],
    );

    const [customizeOpen, setCustomizeOpen] = React.useState(false);
    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };
    // Fall back to the default tab if the last dashboard is deleted or unassigned while shown.
    useEffect(() => {
        if (sources.length === 0 && value === CUSTOM_TAB) setValue(YTD_TAB);
    }, [sources.length, value]);
    const {
        start: secondTabStart,
        data: secondTabData,
        isLoading: secondTabLoading,
    } = useAPIClientRequest(ApiClient.Reports.dashboardLive);
    useEffect(() => {
        secondTabStart();
    }, [secondTabStart]);

    // Building dashboards is for admins; everyone else views the ones assigned to them.
    const { isAdmin } = useAuthenticatedUser();
    const showCustomiseButton = isAdmin && (value === YTD_TAB || value === CUSTOM_TAB);

    return (
        <TabContext value={value}>
            <Box sx={{ px: 3, pt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {currentYear} {t('Modules.Main.Dashboard.campaigns')}
                    </Typography>
                    {showCustomiseButton && (
                        <Tooltip
                            title={
                                activeConfig
                                    ? 'Customise your dashboard'
                                    : 'Create a custom dashboard'
                            }
                        >
                            <IconButton onClick={() => setCustomizeOpen(true)}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}>
                    <Tabs value={value} onChange={handleChange}>
                        <Tab
                            label="YTD Aggregate"
                            value={YTD_TAB}
                            sx={{ textTransform: 'none', minHeight: 40, py: 1 }}
                        />
                        {sources.length > 0 && (
                            <Tab
                                label={
                                    <DashboardTabLabel
                                        name={active?.name ?? 'Custom'}
                                        sources={sources}
                                        activeKey={activeKey}
                                        onSelect={key => {
                                            select(key);
                                            setValue(CUSTOM_TAB);
                                        }}
                                    />
                                }
                                value={CUSTOM_TAB}
                                sx={{ textTransform: 'none', minHeight: 40, py: 1 }}
                            />
                        )}
                        <Tab
                            label="Live Campaigns"
                            value={LIVE_TAB}
                            sx={{ textTransform: 'none', minHeight: 40, py: 1 }}
                        />
                    </Tabs>
                </Box>
            </Box>
            <TabPanel value={YTD_TAB}>
                <FirstTab config={defaultConfig} />
            </TabPanel>
            {activeConfig && (
                <TabPanel value={CUSTOM_TAB}>
                    <FirstTab config={activeConfig} onLayoutChange={onLayoutChange} />
                </TabPanel>
            )}
            <TabPanel value={LIVE_TAB}>
                <SecondTab data={secondTabData?.data ?? {}} isLoading={secondTabLoading} />
            </TabPanel>
            {/* Seeded from whatever is on screen, so customising an assigned dashboard forks it
                into the viewer's own rather than starting from the built-in defaults. */}
            <DashboardCustomizationDialog
                open={customizeOpen}
                value={activeConfig ?? undefined}
                onClose={() => setCustomizeOpen(false)}
                onApplied={() => {
                    select(LOCAL_SOURCE_KEY);
                    setValue(CUSTOM_TAB);
                }}
            />
        </TabContext>
    );
};
const setup: PageConfig = {
    id: 'DashboardHomePage',
    component: MainDashboardPage,
    systemSearch: {
        icon: dashboardIcon,
        saveAsRecentPage: true,
        title: 'Pages.DashboardHome.metaTitle',
        type: 'page',
        aliases: 'SystemSearch.aliases.dashboardPage',
        breadcrumbs: [
            {
                label: 'Pages.DashboardHome.metaTitle',
            },
        ],
        route: PostAuthRoutes.Dashboard.Dashboard,
        permissions: [PermissionName.DashboardReporting],
    },
};

export default setup;
